import { useState } from 'react';
import { Badge, Button } from '@staff-app/ui';
import type { StripeCustomerBilling } from '../hooks/useBillingConsole';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { todayCentral } from '@/features/opticlock/lib/month';
import {
  DUNNING_EXCLUSION_META_KEY,
  DunningExclusionDialog,
  PAUSED,
  RESUMED,
  STOPPED,
  dayLabel,
  type DunningDialogMode,
} from './DunningExclusionDialog';

// Re-exported so existing importers keep one place to reach them.
export {
  DUNNING_EXCLUSION_META_KEY,
  DUNNING_EXCLUSION_UNTIL_META_KEY,
  dayLabel,
} from './DunningExclusionDialog';

/**
 * Who sees and may use the Dunning actions at all: ROOT + BILLING (requester, 2026-09-23) —
 * support does not. Changing it is this list PLUS the resolver's `@RequireRoles` on
 * staffBillingSetDunningExclusion — hiding is a courtesy, the resolver is the control, and the
 * two must be changed together. The Dunning Review list's row + bulk actions read it too.
 */
export const DUNNING_CONTROL_ROLES: string[] = [ADMIN_ROLES.ROOT, ADMIN_ROLES.BILLING];

/**
 * Billing Console → Dunning control, at the right end of the action-tab row so it is visible
 * whichever tab is open.
 *
 * "Exclude from dunning" opens a choice of two (requester, 2026-09-23):
 *   - **Stop** — never falls into the dunning process: `staff-app-dunning-exclusion = true`.
 *   - **Pause until a date** — `= pause` plus `staff-app-dunning-exclusion-until-date =
 *     YYYY-MM-DD` (US Central). After that day staff-app's hourly expiry job writes `false`.
 * "Resume dunning" writes `false` and clears the date.
 *
 * The dialog itself is `DunningExclusionDialog`, shared with the Dunning Exclusion List's row
 * and bulk edit, so the two places cannot drift in what they write.
 *
 * Acts on the RESOLVED customer (`customer.stripeId`), never the raw text in the bar: the
 * console clears `customer` the moment the id changes, so this can never write to a customer
 * other than the one the identity line names.
 */
export function DunningExclusionControl({
  customer,
  onChanged,
  today: todayProp,
}: {
  customer: StripeCustomerBilling | null;
  /** Stripe's read-back after a successful write. */
  onChanged: (value: string | null, untilDate: string | null) => void;
  /** Today in US Central, `YYYY-MM-DD`. Injectable so specs never depend on the real date. */
  today?: string;
}) {
  const me = useAuthStore((s) => s.adminUser);
  const allowed = !!me?.roles?.some((r) => DUNNING_CONTROL_ROLES.includes(r));
  const [mode, setMode] = useState<DunningDialogMode | null>(null);

  const today = todayProp ?? todayCentral();
  const current = customer?.dunningExclusion ?? null;
  const currentUntil = customer?.dunningExclusionUntil ?? null;
  const excluded = current === STOPPED || current === PAUSED;
  // Past its day (or unreadable) but the hourly job has not flipped it yet.
  const pauseEnded = current === PAUSED && (!currentUntil || currentUntil < today);

  // Not rendered at all — no badge either — for a role that cannot use it.
  if (!allowed) return null;

  if (!customer) {
    return (
      <div className="flex items-center gap-2" data-testid="billing-dunning-exclusion">
        <Button
          variant="outline"
          size="sm"
          disabled
          title="Enter a customer Stripe ID above first"
          data-testid="billing-dunning-exclusion-exclude-btn"
        >
          {'Exclude from dunning'}
        </Button>
      </div>
    );
  }

  const badge =
    current === STOPPED ? (
      <Badge variant="destructive" data-testid="billing-dunning-exclusion-badge">
        {'Dunning stopped'}
      </Badge>
    ) : current === PAUSED ? (
      <Badge
        variant="warning"
        title={pauseEnded ? 'The hourly job will set this back to false shortly.' : undefined}
        data-testid="billing-dunning-exclusion-badge"
      >
        {pauseEnded
          ? `Pause ended${currentUntil ? ` ${dayLabel(currentUntil)}` : ''} — resuming`
          : `Dunning paused until ${dayLabel(currentUntil as string)}`}
      </Badge>
    ) : current === RESUMED ? (
      <Badge variant="secondary" data-testid="billing-dunning-exclusion-badge">
        {'Dunning active'}
      </Badge>
    ) : current === null ? (
      <Badge variant="outline" data-testid="billing-dunning-exclusion-badge">
        {'Dunning: not set'}
      </Badge>
    ) : (
      <Badge
        variant="warning"
        title={`Unrecognised value in Stripe metadata "${DUNNING_EXCLUSION_META_KEY}"`}
        data-testid="billing-dunning-exclusion-badge"
      >
        {`Dunning: "${current}"`}
      </Badge>
    );

  const label = customer.companyName || customer.ownerEmail || customer.accountId;

  return (
    <div className="flex items-center gap-2" data-testid="billing-dunning-exclusion">
      {badge}
      <Button
        variant={excluded ? 'outline' : 'destructive'}
        size="sm"
        onClick={() => setMode('exclude')}
        data-testid="billing-dunning-exclusion-exclude-btn"
      >
        {excluded ? 'Change exclusion' : 'Exclude from dunning'}
      </Button>
      {excluded ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMode('resume')}
          data-testid="billing-dunning-exclusion-resume-btn"
        >
          {'Resume dunning'}
        </Button>
      ) : null}

      <DunningExclusionDialog
        mode={mode}
        targets={[{ stripeId: customer.stripeId, label }]}
        today={today}
        onClose={() => setMode(null)}
        onApplied={(applied) => onChanged(applied[0].value, applied[0].untilDate)}
      />
    </div>
  );
}
